import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  BedDouble, Bath, Car, Ruler, MapPin, ArrowLeft, Check, X, ChevronLeft, ChevronRight,
  Phone, Mail,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { formatPrice } from "@/lib/format";
import { PropertyMap } from "@/components/PropertyMap";
import { PropertyCard } from "@/components/PropertyCard";
import { RichContent } from "@/components/RichContent";
import { FollowUs } from "@/components/FollowUs";
import { Seo } from "@/components/Seo";
import { absUrl, truncate, SITE_URL, ogImage } from "@/lib/seo";
import { FadeUp, Eyebrow } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export default function PropertyDetail() {
  const { id } = useParams();
  const [property, setProperty] = useState(null);
  const [similar, setSimilar] = useState([]);
  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setProperty(null);
    setActive(0);
    api.get(`/properties/${id}`)
      .then((r) => {
        setProperty(r.data);
        api.get("/properties", { params: { status: r.data.status } })
          .then((res) => setSimilar(res.data.filter((p) => p.id !== r.data.id).slice(0, 3)))
          .catch(() => {});
      })
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="pt-40 pb-32 text-center" data-testid="property-not-found">
        <Seo title="Property Not Found" path={`/property/${id}`} noindex />
        <h1 className="font-serif text-4xl">Property not found</h1>
        <Link to="/listings" className="mt-6 inline-block text-brand font-semibold">Back to listings</Link>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="pt-40 pb-32 text-center" data-testid="property-loading">
        <span className="font-mono text-xs uppercase tracking-[0.3em] text-muted-foreground animate-pulse">Loading property</span>
      </div>
    );
  }

  const images = property.images?.length ? property.images : [];
  const specs = [
    property.bedrooms > 0 && { icon: BedDouble, label: "Bedrooms", value: property.bedrooms },
    property.bathrooms > 0 && { icon: Bath, label: "Bathrooms", value: property.bathrooms },
    property.garages > 0 && { icon: Car, label: "Parking", value: property.garages },
    property.area_sqm > 0 && { icon: Ruler, label: "Floor Area", value: `${property.area_sqm} sqm` },
  ].filter(Boolean);

  const submitInquiry = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/inquiries", {
        ...form,
        inquiry_type: "Property Inquiry",
        property_id: property.id,
        property_title: property.title,
      });
      toast.success("Inquiry sent — our team will contact you shortly.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const propertyLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: property.title,
    description: truncate(property.description, 300),
    image: images.map(absUrl).slice(0, 5),
    url: `${SITE_URL}/property/${property.id}`,
    category: `${property.property_type} for ${property.status === "rent" ? "Rent" : "Sale"}`,
    brand: { "@type": "Organization", name: "SBS Properties" },
    offers: {
      "@type": "Offer",
      price: property.price,
      priceCurrency: "KES",
      availability: "https://schema.org/InStock",
      url: `${SITE_URL}/property/${property.id}`,
    },
    additionalProperty: [
      property.bedrooms > 0 && { "@type": "PropertyValue", name: "Bedrooms", value: property.bedrooms },
      property.bathrooms > 0 && { "@type": "PropertyValue", name: "Bathrooms", value: property.bathrooms },
      property.garages > 0 && { "@type": "PropertyValue", name: "Parking", value: property.garages },
      property.area_sqm > 0 && { "@type": "PropertyValue", name: "Floor Area (sqm)", value: property.area_sqm },
    ].filter(Boolean),
  };

  return (
    <div data-testid="property-detail-page" className="pt-24 md:pt-28 pb-24">
      <Seo
        type="article"
        path={`/property/${property.id}`}
        title={`${property.title} — ${property.property_type} ${property.status === "rent" ? "for Rent" : "for Sale"} in ${property.city}`}
        description={truncate(property.description)}
        image={ogImage(images[0])}
        jsonLd={propertyLd}
      />
      <div className="mx-auto max-w-7xl px-4 sm:px-8">
        <Link to="/listings" data-testid="property-back-link" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-brand transition-colors">
          <ArrowLeft className="h-4 w-4" /> Back to listings
        </Link>

        {/* Meta banner */}
        <FadeUp className="mt-6 flex flex-wrap items-start justify-between gap-6">
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <span className={`px-3 py-1 text-[11px] font-mono uppercase tracking-[0.15em] font-bold ${property.status === "rent" ? "bg-brand-emerald text-ivory" : "bg-brand text-ivory"}`}>
                {property.status === "rent" ? "For Rent" : "For Sale"}
              </span>
              <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-muted-foreground">
                {property.property_type} · {property.city}
              </span>
            </div>
            <h1 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight max-w-3xl">{property.title}</h1>
            <p className="mt-3 flex items-center gap-2 text-muted-foreground">
              <MapPin className="h-4 w-4 text-brand shrink-0" /> {property.location}
            </p>
          </div>
          <div className="bg-obsidian px-6 py-4 text-right">
            <div className="font-display text-2xl sm:text-3xl font-semibold text-ivory" data-testid="property-price">
              {formatPrice(property.price)}
            </div>
            {property.price_label && <div className="text-xs text-ivory/60 mt-1">{property.price_label}</div>}
          </div>
        </FadeUp>

        {/* Gallery */}
        <FadeUp delay={0.1} className="mt-10">
          <div className="relative overflow-hidden border hairline cursor-zoom-in" onClick={() => setLightbox(true)} data-testid="property-gallery-main">
            <img src={images[active]} alt={property.title} className="w-full aspect-[16/9] object-cover" />
            <div className="absolute inset-0 ring-1 ring-inset ring-black/10 pointer-events-none" />
          </div>
          {images.length > 1 && (
            <div className="mt-4 flex gap-3 overflow-x-auto pb-2" data-testid="property-gallery-thumbs">
              {images.map((img, i) => (
                <button
                  key={`${img}-${i}`}
                  data-testid={`gallery-thumb-${i}`}
                  onClick={() => setActive(i)}
                  className={`shrink-0 overflow-hidden border-2 transition-colors ${i === active ? "border-brand" : "border-transparent opacity-60 hover:opacity-100"}`}
                >
                  <img src={img} alt="" className="h-20 w-32 object-cover" />
                </button>
              ))}
            </div>
          )}
        </FadeUp>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-14">
          <div className="lg:col-span-2">
            {/* Specs */}
            {specs.length > 0 && (
              <FadeUp
                style={{ gridTemplateColumns: `repeat(${specs.length}, minmax(0, 1fr))` }}
                className="grid gap-px bg-foreground/10 border hairline"
                data-testid="property-specs"
              >
                {specs.map(({ icon: Icon, label, value }) => (
                  <div key={label} className="bg-white px-6 py-6 text-center">
                    <Icon className="mx-auto h-6 w-6 text-brand" />
                    <div className="mt-3 font-display text-xl font-semibold">{value}</div>
                    <div className="mt-1 text-[11px] font-mono uppercase tracking-[0.15em] text-muted-foreground">{label}</div>
                  </div>
                ))}
              </FadeUp>
            )}

            <FadeUp className="mt-12">
              <Eyebrow>About This Property</Eyebrow>
              {/<[a-z][\s\S]*>/i.test(property.description || "") ? (
                <RichContent
                  testId="property-description"
                  className="article-body mt-5 text-muted-foreground text-base sm:text-lg"
                  html={property.description}
                />
              ) : (
                <p className="mt-5 text-muted-foreground leading-relaxed text-base sm:text-lg whitespace-pre-line" data-testid="property-description">
                  {property.description}
                </p>
              )}
              <FollowUs className="mt-8" />
            </FadeUp>

            {property.features?.length > 0 && (
              <FadeUp className="mt-12">
                <Eyebrow>Amenities</Eyebrow>
                <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="property-amenities">
                  {property.features.map((f) => (
                    <span key={f} className="flex items-center gap-3 border hairline bg-white px-4 py-3 text-sm">
                      <Check className="h-4 w-4 text-brand-emerald shrink-0" /> {f}
                    </span>
                  ))}
                </div>
              </FadeUp>
            )}

            <FadeUp className="mt-12">
              <Eyebrow>Location</Eyebrow>
              <div className="mt-5 border hairline overflow-hidden">
                <PropertyMap lat={property.lat} lng={property.lng} zoom={15} height="400px" />
              </div>
              <a
                data-testid="property-directions-link"
                href={`https://www.google.com/maps/dir/?api=1&destination=${property.lat},${property.lng}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-brand"
              >
                Get Directions <MapPin className="h-4 w-4" />
              </a>
            </FadeUp>
          </div>

          {/* Inquiry form */}
          <FadeUp delay={0.15}>
            <div className="lg:sticky lg:top-28 bg-obsidian text-ivory p-8 grain relative" data-testid="property-inquiry-panel">
              <h3 className="font-serif text-2xl">Schedule a Tour</h3>
              <p className="mt-2 text-sm text-ivory/60">Send an inquiry and our team will respond promptly.</p>
              <form onSubmit={submitInquiry} className="mt-6 space-y-4">
                <Input data-testid="inquiry-name-input" required placeholder="Full Name" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="bg-white/5 border-white/15 text-ivory placeholder:text-ivory/40 rounded-none" />
                <Input data-testid="inquiry-email-input" required type="email" placeholder="Email Address" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="bg-white/5 border-white/15 text-ivory placeholder:text-ivory/40 rounded-none" />
                <Input data-testid="inquiry-phone-input" placeholder="Phone Number" value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="bg-white/5 border-white/15 text-ivory placeholder:text-ivory/40 rounded-none" />
                <Textarea data-testid="inquiry-message-input" required rows={4} placeholder={`I'm interested in "${property.title}"…`} value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="bg-white/5 border-white/15 text-ivory placeholder:text-ivory/40 rounded-none" />
                <button
                  data-testid="inquiry-submit-button"
                  disabled={sending}
                  className="w-full bg-brand py-3.5 text-sm font-semibold text-ivory hover:bg-brand-hover transition-colors disabled:opacity-50"
                >
                  {sending ? "Sending…" : "Send Inquiry"}
                </button>
              </form>
              <div className="mt-6 flex flex-col gap-2 border-t border-white/10 pt-6 text-sm text-ivory/70">
                <a href="tel:+254709746298" data-testid="inquiry-call-link" className="flex items-center gap-2 hover:text-brand-gold transition-colors">
                  <Phone className="h-4 w-4 text-brand-gold" /> +(254) 709 746 298
                </a>
                <a href="mailto:info@sbsproperties.co.ke" data-testid="inquiry-email-link" className="flex items-center gap-2 hover:text-brand-gold transition-colors">
                  <Mail className="h-4 w-4 text-brand-gold" /> info@sbsproperties.co.ke
                </a>
              </div>
            </div>
          </FadeUp>
        </div>

        {/* Similar */}
        {similar.length > 0 && (
          <div className="mt-24" data-testid="similar-properties">
            <FadeUp>
              <Eyebrow>Keep Exploring</Eyebrow>
              <h2 className="mt-4 font-serif text-3xl sm:text-4xl tracking-tight">Similar Properties</h2>
            </FadeUp>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-8">
              {similar.map((p, i) => <PropertyCard key={p.id} property={p} index={i} />)}
            </div>
          </div>
        )}
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-obsidian/95 flex items-center justify-center p-4"
            onClick={() => setLightbox(false)}
            data-testid="gallery-lightbox"
          >
            <button data-testid="lightbox-close-btn" className="absolute top-6 right-6 text-ivory/70 hover:text-ivory p-2" onClick={() => setLightbox(false)}>
              <X className="h-7 w-7" />
            </button>
            {images.length > 1 && (
              <>
                <button data-testid="lightbox-prev-btn" className="absolute left-4 sm:left-8 text-ivory/70 hover:text-ivory p-2"
                  onClick={(e) => { e.stopPropagation(); setActive((active - 1 + images.length) % images.length); }}>
                  <ChevronLeft className="h-9 w-9" />
                </button>
                <button data-testid="lightbox-next-btn" className="absolute right-4 sm:right-8 text-ivory/70 hover:text-ivory p-2"
                  onClick={(e) => { e.stopPropagation(); setActive((active + 1) % images.length); }}>
                  <ChevronRight className="h-9 w-9" />
                </button>
              </>
            )}
            <motion.img
              key={active}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              src={images[active]}
              alt={property.title}
              className="max-h-[85vh] max-w-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
